// ============================================================
// CLUBHUB – Einstellungen: die Bereichsspalte
// ============================================================
// Eine eigene Navigationsspalte INNERHALB der Einstellungen, mit
// denselben Bauteilen wie die Hauptnavigation (Muster aus dem Agent).
// Die Gruppe „Bereiche“ spiegelt die Hauptnavigation — wer eine
// Einstellung sucht, sucht sie dort, wo die Funktion wohnt.
// Auf schmalen Schirmen wird aus der Spalte eine liegende Leiste.
// ============================================================

"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Building2,
  CalendarDays,
  Database,
  Globe,
  Landmark,
  Lock,
  Mail,
  Plug,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { GROUP_LABELS, type SettingsGroup } from "@/lib/einstellungen/types";

const ICONS: Record<string, LucideIcon> = {
  Building2,
  CalendarDays,
  Database,
  Globe,
  Landmark,
  Lock,
  Mail,
  Plug,
  ShieldCheck,
  UserRound,
  Users,
};

export type AreaLink = {
  key: string;
  title: string;
  group: SettingsGroup;
  icon: string;
  href: string;
};

const GROUP_ORDER: SettingsGroup[] = ["verein", "bereiche", "system", "persoenlich"];

export function AreaNav({ areas }: { areas: AreaLink[] }) {
  const pathname = usePathname();

  return (
    <nav
      aria-label="Einstellungsbereiche"
      className={cn(
        "shrink-0",
        "lg:w-56",
        "-mx-1 flex gap-1 overflow-x-auto px-1 pb-2 lg:mx-0 lg:block lg:space-y-5 lg:overflow-y-auto lg:px-0 lg:pb-0",
      )}
    >
      {GROUP_ORDER.map((group) => {
        const inGroup = areas.filter((area) => area.group === group);
        if (inGroup.length === 0) return null;

        return (
          <div key={group} className="contents lg:block lg:space-y-0.5">
            <p className="text-overline text-text-tertiary hidden px-2.5 pb-1 uppercase lg:block">
              {GROUP_LABELS[group]}
            </p>
            {inGroup.map((area) => {
              const active = pathname === `/einstellungen/${area.key}` || pathname.startsWith(`/einstellungen/${area.key}/`);
              const Icon = ICONS[area.icon] ?? Settings;

              return (
                <Link
                  key={area.key}
                  href={area.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "group text-body-sm relative flex shrink-0 items-center gap-2.5 rounded-lg px-2.5 py-[7px] whitespace-nowrap",
                    "duration-fast ease-standard transition-colors",
                    active
                      ? "bg-bg-tertiary text-text-primary font-medium"
                      : "text-text-secondary hover:bg-bg-secondary hover:text-text-primary",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "bg-accent absolute top-1/2 left-0 hidden h-4 w-[3px] -translate-y-1/2 rounded-r-full lg:block",
                      "duration-fast transition-opacity",
                      active ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <Icon
                    className={cn(
                      "duration-fast h-4 w-4 shrink-0 transition-colors",
                      active ? "text-accent" : "text-text-tertiary group-hover:text-text-secondary",
                    )}
                    aria-hidden
                  />
                  {area.title}
                </Link>
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
